'use client'
import {ENDPOINTS} from "@/app/endpoints/wpEndpoints";
import dynamic from "next/dynamic";
import {FormEvent, useState} from "react";
import {useTracking} from "@/app/contexts/tracking-context";
import {ArticleTrackingParams} from "@/app/utils/article-tracking";
import Image from "next/image";
import logoRM from "@/public/images/RM_logo_V2_mini.svg";
import AppButton from "@/app/components/atomic/molecules/AppButton";

const AppInput = dynamic(() => import('@/app/components/atomic/Input/AppInput'));

interface FormData {
    name: string;
    email: string;
    message: string;
}

export function PageCommentForm({wpPageId, articleParams}: { wpPageId: string, articleParams?: ArticleTrackingParams }) {
    const [formData, setFormData] = useState<FormData>({
        name: '',
        email: '',
        message: '',
    });

    const [emailError, setEmailError] = useState<string>('');
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
    const [hasError, setHasError] = useState<boolean>(false);
    const [showForm, setShowForm] = useState<boolean>(false);
    const {track} = useTracking();

    const handleChange = (inputReturn: any) => {
        setFormData({...formData, [inputReturn.fieldName]: inputReturn.value});
    };

    const validateEmail = (email: string): boolean => {
        const re = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        return re.test(String(email).toLowerCase());
    };

    const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (isSubmitting) return;

        if (formData.email && !validateEmail(formData.email)) {
            setEmailError('Votre email est invalide');
            return;
        } else {
            setEmailError('');
            setHasError(false);
            setIsSubmitted(false);
            await postNewComment();
        }
    };

    const postNewComment = async () => {
        setIsSubmitting(true);
        try {
            const response = await fetch(ENDPOINTS.WP_CACHE_COMMENTS.POST_NEW_COMMENT, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    // Add any necessary authentication headers here
                },
                body: JSON.stringify({
                    pageId: wpPageId,
                    name: formData.name,
                    email: formData.email,
                    message: formData.message,
                }),
            });

            if (response.ok) {
                setIsSubmitted(true);
                setFormData({
                    name: '',
                    email: '',
                    message: '',
                });

                track({event: 'submit_commentaire', ...articleParams})
            } else {
                setHasError(true);
            }
        } catch (error) {
            setHasError(true);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="flex flex-col gap-rm-20 rounded-2xl p-4 bg-white text-pale">
            <div className="flex sm:items-center justify-between flex-col sm:flex-row gap-rm-15">
                <div className="flex items-center gap-rm-10">
                    <div className="w-[60px] h-[60px] bg-rm-blue/10 rounded-xl flex items-center justify-center">
                        <Image src={logoRM} alt={`Reassurez-moi`} width={40} height={40} />
                    </div>
                    <label
                        htmlFor="message"
                        className="flex flex-col justify-center items-start text-rm-16"
                    >
                        <span className="font-bold title-look p-0">
                            Posez une question
                        </span>
                        <span>
                            Un expert vous répondra
                        </span>
                    </label>
                </div>

                {!showForm &&
                    <AppButton
                        theme="whiteBlur"
                        arrowPosition="none"
                        onClick={(e) => {
                            e.preventDefault();
                            setShowForm(true);
                        }}
                    >
                        Rédiger une question
                    </AppButton>
                }
            </div>

            {showForm && (
                <div className="flex flex-col gap-4 items-center">
                    <textarea
                        className="h-40 p-4 rounded overflow-hidden w-full transition-all duration-500
                                    border border-rm-gray-light hover:border-rm-gray focus-within:border-rm-gray"
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={(e) => handleChange({value: e.target.value, fieldName: 'message'})}
                        required
                    />

                    <div className="flex-1 flex flex-col w-full">
                        <label className="font-semibold w-full" htmlFor="name">Votre nom</label>
                        <AppInput
                            fieldName="name"
                            onChange={(inputReturn) => handleChange(inputReturn)}
                            value={formData.name}
                            borders="neutral"
                            required
                        />
                    </div>

                    <div className="flex-1 flex flex-col w-full">
                        <label className="font-semibold w-full" htmlFor="email">Votre email <small>(facultatif)</small></label>
                        <AppInput
                            fieldName="email"
                            onChange={(inputReturn) => handleChange(inputReturn)}
                            value={formData.email}
                            borders="neutral"
                        />
                        {emailError && <p className="text-red-500">{emailError}</p>}
                    </div>

                    <AppButton
                        theme="whiteBlur"
                        arrowPosition="none"
                        type="submit"
                        disabled={isSubmitting}
                    >
                        Poser votre question
                    </AppButton>
                </div>
            )}

            {isSubmitted &&
                <div className="text-green-500 font-semibold mx-auto">
                    Votre commentaire a été envoyé avec succès !
                </div>
            }

            {hasError &&
                <div className="text-red-500 font-semibold mx-auto">
                    Une erreur est survenue, veuillez réessayer plus tard.
                </div>
            }
        </form>
    );
}
